import type { OrderDashboardIssue } from '@/api/hooks/useOrderDashboard'
import { type IssueSortOrder, compareIssueDates } from './issueCounts'
import { issueGroupKeys, issueOrderNumber } from './issueFields'

/** One unique issue: every open failure in the window that shares its key. */
export interface IssueGroup {
  /** The tracked issue key, or the lone log's own key when it has none. */
  key: string
  /** Newest first. */
  logs: OrderDashboardIssue[]
  /** The failure the card speaks with: the newest that is not a skipped
   * event, since a skip only echoes the failure that blocked it. */
  latest: OrderDashboardIssue
  /** How many transactions it holds up; Retry acts on just one. */
  transactions: number
  /** The orders it is holding up, each once. */
  orderNumbers: string[]
}

/**
 * Folds the window's failures into one entry per unique issue. A log that
 * fails for two reasons is an occurrence of both, so it shows under each.
 */
export function groupIssues(logs: OrderDashboardIssue[]): IssueGroup[] {
  const members = new Map<string, OrderDashboardIssue[]>()
  for (const log of logs) {
    for (const key of issueGroupKeys(log)) {
      members.set(key, [...(members.get(key) ?? []), log])
    }
  }
  // A key only exists once a log has been filed under it, so every group has
  // a latest; the guard is for the type checker.
  return [...members.entries()].flatMap(([key, group]) => {
    const sorted = [...group].sort(compareIssueDates('newest'))
    const latest = sorted.find((log) => !log.skipped) ?? sorted[0]
    if (!latest) return []
    const numbers = sorted.map(issueOrderNumber).filter((n) => n !== null)
    return [
      {
        key,
        logs: sorted,
        latest,
        orderNumbers: [...new Set(numbers)],
        transactions: new Set(sorted.map((log) => log.log_group_id)).size,
      },
    ]
  })
}

/** Sorts by each issue's latest failure, undated issues last either way. */
export function sortIssueGroups(
  groups: IssueGroup[],
  order: IssueSortOrder,
): IssueGroup[] {
  const compare = compareIssueDates(order)
  return [...groups].sort((a, b) => compare(a.latest, b.latest))
}

/** The orders a bulk retry would send for this issue, each once. */
export function bulkRetryLogGroupIds(group: IssueGroup): string[] {
  const ids = group.logs
    .filter(
      (log) =>
        log.metadata.type === 'order' &&
        log.status === 'failed' &&
        !log.retried &&
        !log.skipped,
    )
    .map((log) => log.log_group_id)
    .filter((id): id is string => !!id)
  return [...new Set(ids)]
}
